import { useState, useEffect, useMemo, useCallback, useRef } from "react";
import { useFormattedInput } from "../../hooks/useFormattedInput";
import { formatCurrency, getTodayLocal } from "../../lib/formato";
import ItemInput from "./ItemInput";
import SelectorFormaPago from "../movimientos/SelectorFormaPago";
import { TrashIcon, PlusIcon } from "../Iconos";

// Modal para registrar o editar una venta (con varias líneas de producto)
// o un abono (un solo monto). El tipo lo decide la prop `type`.
const AddTransactionModal = ({ type, clienteId, onSave, onClose, transactionToEdit = null }) => {
  const isVenta = type === "venta";

  const [fecha, setFecha] = useState(getTodayLocal());
  const [items, setItems] = useState([]);
  const [guardando, setGuardando] = useState(false);
  const [tipoPagoVenta, setTipoPagoVenta] = useState(
    transactionToEdit && isVenta && transactionToEdit.tipo_pago
      ? transactionToEdit.tipo_pago
      : "credito"
  );

  // Cómo se pagó. Solo aplica a las ventas de contado y a los abonos.
  const [formaPago, setFormaPago] = useState(
    (transactionToEdit && transactionToEdit.metodo_pago) || "efectivo"
  );

  const [montoValor, montoMostrado, alCambiarMonto] = useFormattedInput(
    transactionToEdit && !isVenta ? transactionToEdit.monto : "",
    true
  );

  const contenedorItems = useRef(null);

  useEffect(() => {
    setFecha(transactionToEdit ? transactionToEdit.fecha.slice(0, 10) : getTodayLocal());

    if (!isVenta) return;

    if (transactionToEdit && transactionToEdit.detalles) {
      setItems(
        transactionToEdit.detalles.map((d) => ({
          id: d.id,
          descripcion: d.producto_descripcion,
          cantidad: parseFloat(d.cantidad) || 0,
          precio: parseFloat(d.precio_unitario) || 0,
        }))
      );
    } else {
      setItems([{ tempId: Date.now(), descripcion: "", cantidad: 1, precio: "" }]);
    }
  }, [transactionToEdit, isVenta]);

  // Al agregar una línea, se baja el scroll para que quede a la vista.
  useEffect(() => {
    if (!contenedorItems.current) return;
    const el = contenedorItems.current;
    requestAnimationFrame(() => {
      el.scrollTop = el.scrollHeight;
    });
  }, [items.length]);

  const handleItemChange = useCallback((index, campo, valor) => {
    setItems((previos) => {
      const nuevos = [...previos];
      const item = { ...nuevos[index] };
      if (campo === "cantidad" || campo === "precio") {
        item[campo] = Math.max(0, parseFloat(valor) || 0);
      } else {
        item[campo] = valor;
      }
      nuevos[index] = item;
      return nuevos;
    });
  }, []);

  const handleAddItem = useCallback(() => {
    setItems((previos) => [
      ...previos,
      { tempId: Date.now(), descripcion: "", cantidad: 1, precio: "" },
    ]);
  }, []);

  const handleRemoveItem = useCallback((indice) => {
    setItems((previos) => previos.filter((_, i) => i !== indice));
  }, []);

  const ventaTotal = useMemo(
    () =>
      items.reduce(
        (suma, item) => suma + (parseFloat(item.cantidad) || 0) * (parseFloat(item.precio) || 0),
        0
      ),
    [items]
  );

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Al editar se respeta la hora original del registro y solo se
    // cambia el día.
    let marcaDeTiempo = fecha;
    if (transactionToEdit && transactionToEdit.fecha) {
      marcaDeTiempo = `${fecha} ${transactionToEdit.fecha.slice(11)}`;
    }

    let datos;
    if (isVenta) {
      const itemsValidos = items.filter(
        (item) =>
          item.descripcion.trim() !== "" &&
          parseFloat(item.cantidad) > 0 &&
          parseFloat(item.precio) >= 0
      );
      if (itemsValidos.length === 0) {
        alert("Agrega al menos un producto con descripción, cantidad y precio válidos.");
        return;
      }
      datos = {
        cliente_id: clienteId,
        productos: itemsValidos,
        tipo_pago: tipoPagoVenta,
        // El servidor la ignora en las ventas a crédito.
        metodo_pago: tipoPagoVenta === "contado" ? formaPago : null,
        fecha: marcaDeTiempo,
      };
    } else {
      if (isNaN(montoValor) || montoValor <= 0) {
        alert("Ingresa un monto válido.");
        return;
      }
      datos = {
        cliente_id: clienteId,
        monto: montoValor,
        metodo_pago: formaPago,
        fecha: marcaDeTiempo,
      };
    }

    if (transactionToEdit) datos.id = transactionToEdit.id;

    // Se bloquea el botón mientras se guarda: un doble clic registraba
    // la misma venta dos veces.
    setGuardando(true);
    try {
      await onSave(isVenta ? "venta" : "abono", datos);
    } finally {
      setGuardando(false);
    }
  };

  const titulo = transactionToEdit
    ? `Editar ${isVenta ? "Venta" : "Abono"}`
    : isVenta
    ? "Registrar Venta"
    : "Registrar Abono";

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content" onClick={(e) => e.stopPropagation()}>
        <h2>{titulo}</h2>
        <form onSubmit={handleSubmit} className="modal-form">
          <div className="form-group">
            <label htmlFor="transaction-date" className="form-label">
              Fecha
            </label>
            <input
              type="date"
              id="transaction-date"
              value={fecha}
              onChange={(e) => setFecha(e.target.value)}
              required
            />
          </div>

          {isVenta ? (
            <>
              <div className="tipo-pago-selector">
                <label>Tipo de Pago:</label>
                <div className="radio-group">
                  <input
                    type="radio"
                    id="credito"
                    name="tipo_pago"
                    value="credito"
                    checked={tipoPagoVenta === "credito"}
                    onChange={() => setTipoPagoVenta("credito")}
                  />
                  <label htmlFor="credito">Crédito</label>
                  <input
                    type="radio"
                    id="contado"
                    name="tipo_pago"
                    value="contado"
                    checked={tipoPagoVenta === "contado"}
                    onChange={() => setTipoPagoVenta("contado")}
                  />
                  <label htmlFor="contado">Contado</label>
                </div>
              </div>

              {tipoPagoVenta === "contado" && (
                <SelectorFormaPago valor={formaPago} onChange={setFormaPago} />
              )}

              <div className="venta-items-container" ref={contenedorItems}>
                {items.map((item, index) => (
                  <div className="venta-item-row" key={item.id || item.tempId}>
                    <ItemInput
                      descripcion={item.descripcion}
                      cantidad={item.cantidad}
                      precio={item.precio}
                      index={index}
                      handleItemChange={handleItemChange}
                    />
                    {items.length > 1 && (
                      <button
                        type="button"
                        className="remove-item-btn"
                        onClick={() => handleRemoveItem(index)}
                        title="Quitar producto"
                      >
                        <TrashIcon />
                      </button>
                    )}
                  </div>
                ))}
              </div>

              <button type="button" className="add-item-btn" onClick={handleAddItem}>
                <PlusIcon /> Agregar producto
              </button>
            </>
          ) : (
            <>
              <SelectorFormaPago valor={formaPago} onChange={setFormaPago} />
              <input
                type="text"
                placeholder="Monto"
                value={montoMostrado}
                onChange={alCambiarMonto}
                required
                className="input-monto"
                inputMode="decimal"
                autoFocus
              />
            </>
          )}

          <div className="modal-actions">
            {isVenta && (
              <div className="venta-total">
                <strong>Total: {formatCurrency(ventaTotal)}</strong>
              </div>
            )}
            <div className="modal-actions-buttons">
              <button type="button" className="btn-cancel" onClick={onClose}>
                Cancelar
              </button>
              <button type="submit" className="btn-submit" disabled={guardando}>
                {guardando ? "Guardando..." : "Guardar"}
              </button>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
};

export default AddTransactionModal;
